import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { DataKeys } from "../../types";
import { useLang } from "../Context";
import { HealthSection } from "../health/HealthSection";
import { HealthSectionHeader } from "../health/HealthSectionHeader";
import { getLocalized, HealthLang, LocalizedString } from "../health/healthTypes";
import {
    HEALTH_BG_LIFECYCLE,
    healthFocusVisible,
    healthSecondaryButton,
    healthButtonArrowFa,
    healthButtonLabel,
} from "../health/healthVisualStyles";
import { educationLocalePath } from "./educationLocalePath";

interface EducationFrameworkSectionProps {
    data: DataKeys;
}

export function EducationFrameworkSection({ data }: EducationFrameworkSectionProps) {
    const { lang } = useLang();
    const hLang = lang as HealthLang;
    const steps: DataKeys[] = data?.steps ?? [];
    const sectionId = "learning-framework";
    const ctaHref = educationLocalePath(
        (data?.ctaHref as string) ?? "/education/medical-sensei",
        hLang
    );

    return (
        <HealthSection
            id={sectionId}
            ariaLabelledBy={`${sectionId}-title`}
            compact
            style={{ backgroundColor: HEALTH_BG_LIFECYCLE }}
        >
            <HealthSectionHeader
                eyebrow={getLocalized(data?.eyebrow, hLang)}
                title={getLocalized(data?.title, hLang)}
                titleId={`${sectionId}-title`}
                subtitle={getLocalized(data?.intro, hLang)}
                subtitleWide
            />

            <div className="relative mt-8 w-full min-w-0">
                <div
                    className="pointer-events-none absolute left-4 right-4 top-4 hidden h-px bg-teal-200/80 lg:block"
                    aria-hidden="true"
                />
                <ol className="flex w-full flex-col gap-8 list-none pl-0 lg:grid lg:grid-cols-5 lg:gap-0">
                    {steps.map((step, index) => (
                        <li
                            key={(step.id as string) ?? index}
                            className="relative flex w-full flex-col items-center border-b border-teal-100/70 pb-8 text-center last:border-b-0 last:pb-0 lg:border-b-0 lg:px-2 lg:pb-0"
                        >
                            <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-teal-500 text-xs font-bold text-white shadow-sm">
                                {String(index + 1).padStart(2, "0")}
                            </span>
                            <h3 className="mt-3 w-full text-center text-sm font-bold leading-snug text-grey-900 [text-align:center] lg:text-base">
                                {getLocalized(step.title as LocalizedString, hLang)}
                            </h3>
                            <p className="mt-2 w-full text-center text-xs leading-relaxed text-grey-600 [text-align:center]">
                                {getLocalized(step.text as LocalizedString, hLang)}
                            </p>
                        </li>
                    ))}
                </ol>
            </div>

            <div className="mt-8 flex justify-start">
                <Link
                    to={ctaHref}
                    className={`${healthSecondaryButton} ${healthFocusVisible} !w-auto max-w-none self-start`}
                >
                    <span className={healthButtonLabel}>
                        {getLocalized(data?.ctaLabel, hLang)}
                    </span>
                    <FontAwesomeIcon icon={faArrowRight} className={healthButtonArrowFa} />
                </Link>
            </div>
        </HealthSection>
    );
}
